// src/components/NotificationPrompt.jsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert
} from '@mui/material';
import {
  NotificationsActive as NotificationsActiveIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import notificationService from '../services/notificationService';
import { useSnackbar } from 'notistack';
import { useThemeSettings } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';

const NotificationPrompt = () => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useThemeSettings();
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    // Nothing to ask an anonymous visitor. Re-runs when the session appears, so a user who
    // signs in still gets the prompt in the same page load.
    if (!isAuthenticated) return;
    checkNotificationStatus();
  }, [isAuthenticated]);

  const checkNotificationStatus = async () => {
    try {
      // Check if notifications are supported
      if (!('Notification' in window)) {
        return;
      }

      // ── PUBLIC PAGES NEVER ASK ────────────────────────────────────────────
      //
      // This used to skip only `/login`, so every OTHER unauthenticated route got the
      // dialog: the onboarding form, the application status page, the activation page, the
      // public alarm view, the estimate approval page. Asking a prospect who has no account
      // to accept push notifications is nonsense, and on the onboarding form it lands on top
      // of the thing they were sent there to fill in.
      //
      // The component is now also gated on `isAuthenticated` at the call site; this list is
      // the second line, for routes that a signed-in user can still reach.
      const PUBLIC_PREFIXES = [
        '/login',
        '/onboarding',
        '/activate',
        '/alarm/',
        '/approve/',
        '/health',
        '/auth-callback',
        '/auth-error',
      ];
      if (PUBLIC_PREFIXES.some((p) => window.location.pathname.startsWith(p))) {
        return;
      }

      // ── HONOUR THE ANSWER WE WERE ALREADY GIVEN ───────────────────────────
      //
      // `notificationPromptNever` and `notificationPromptDismissed` were being WRITTEN by
      // handleNever/handleLater and never read back, so "Never" meant "ask me again in five
      // seconds". Reading them here is what makes both buttons mean something.
      try {
        if (localStorage.getItem('notificationPromptNever') === 'true') {
          return;
        }
        const dismissedAt = parseInt(localStorage.getItem('notificationPromptDismissed'), 10);
        if (!Number.isNaN(dismissedAt) && Date.now() - dismissedAt < 24 * 60 * 60 * 1000) {
          return;
        }
      } catch {
        // A blocked localStorage must not make the prompt un-dismissable, but it also must
        // not crash the page. Fall through and ask.
      }

      // Check if already granted
      if (Notification.permission === 'granted') {
        // Initialize and subscribe
        const initialized = await notificationService.initialize();
        if (initialized) {
          const subscribed = await notificationService.isSubscribed();
          if (!subscribed) {
            // Show prompt to subscribe
            setOpen(true);
          }
        }
        return;
      }

      // Check if permission is default (not asked yet)
      if (Notification.permission === 'default') {
        // Show prompt after a delay (don't be too aggressive)
        setTimeout(() => {
          setOpen(true);
        }, 5000);
      }
    } catch (error) {
      console.error('Error checking notification status:', error);
    }
  };

  const handleEnable = async () => {
    try {
      setLoading(true);

      // Initialize notification service
      const initialized = await notificationService.initialize();
      if (!initialized) {
        throw new Error('Failed to initialize notification service');
      }

      // Subscribe to push notifications
      const subscribed = await notificationService.subscribe();
      if (subscribed) {
        enqueueSnackbar(t ? t('Notifications.enabled_success') : 'Push notifications enabled successfully!', { variant: 'success' });
        setOpen(false);
      } else {
        throw new Error('Failed to subscribe to push notifications');
      }
    } catch (error) {
      console.error('Failed to enable notifications:', error);
      enqueueSnackbar(t ? t('Notifications.enable_failed') : 'Failed to enable push notifications. Please check your browser settings.', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleLater = () => {
    setOpen(false);
    // Show again after 24 hours
    localStorage.setItem('notificationPromptDismissed', Date.now().toString());
  };

  const handleNever = () => {
    setOpen(false);
    localStorage.setItem('notificationPromptNever', 'true');
  };

  return (
    <Dialog
      open={open}
      onClose={handleLater}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2
        }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <NotificationsActiveIcon color="primary" />
          <Typography variant="h6">{t ? t('Notifications.prompt_title') : 'Enable Push Notifications'}</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {t ? t('Notifications.prompt_description') : 'Stay up-to-date with real-time notifications about:'}
        </DialogContentText>

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            • {t ? t('Notifications.feature_tickets') : 'Ticket updates and assignments'}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            • {t ? t('Notifications.feature_devices') : 'Device status changes'}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            • {t ? t('Notifications.feature_files') : 'File sharing and uploads'}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            • {t ? t('Notifications.feature_alerts') : 'Critical system alerts'}
          </Typography>
        </Box>

        <Alert severity="info" sx={{ mt: 2 }}>
          {t ? t('Notifications.critical_sound_info') : 'Critical alerts will play a sound even when your device is muted, ensuring you never miss important notifications.'}
        </Alert>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleNever} color="inherit">
          {t ? t('Notifications.button_never') : 'Never'}
        </Button>
        <Button onClick={handleLater} color="inherit">
          {t ? t('Notifications.button_later') : 'Later'}
        </Button>
        <Button
          onClick={handleEnable}
          variant="contained"
          disabled={loading}
        >
          {loading ? (t ? t('Notifications.button_enabling') : 'Enabling...') : (t ? t('Notifications.button_enable') : 'Enable Notifications')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NotificationPrompt;
